/**
 * Intent Response Parser
 *
 * Utilities for parsing intent objects from LLM responses.
 * Works with the simplified intent-based prompt format.
 */

/**
 * Parse intent from LLM response text
 *
 * Expected format:
 * [Explanation text with markdown]
 *
 * <<<INTENT>>>
 * {
 *   "operations": [...]
 * }
 * <<<END_INTENT>>>
 *
 * [Optional additional text]
 *
 * @param {string} responseText - Raw LLM response text
 * @returns {Object} - { intent, explanation, error, warning }
 */
export function parseIntentFromLLMResponse(responseText) {
  const result = {
    intent: null,
    explanation: '',
    error: null,
    warning: null,
  };

  // Validate input
  if (!responseText || typeof responseText !== 'string') {
    result.error = 'Invalid response: empty or not a string';
    return result;
  }

  // Check for markers
  const hasStartMarker = responseText.includes('<<<INTENT>>>');
  const hasEndMarker = responseText.includes('<<<END_INTENT>>>');

  // If no markers, try to detect and parse raw JSON (fallback)
  if (!hasStartMarker && !hasEndMarker) {
    // Try to find and extract JSON object with "operations" field
    // Look for { ... "operations" ... } pattern
    const jsonStartIdx = responseText.indexOf('{');

    if (jsonStartIdx !== -1) {
      // Try to find the matching closing brace by counting braces
      let braceCount = 0;
      let jsonEndIdx = -1;

      for (let i = jsonStartIdx; i < responseText.length; i++) {
        if (responseText[i] === '{') braceCount++;
        else if (responseText[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEndIdx = i + 1;
            break;
          }
        }
      }

      if (jsonEndIdx !== -1) {
        try {
          const jsonText = responseText.substring(jsonStartIdx, jsonEndIdx);
          const parsed = JSON.parse(jsonText);

          if (parsed.operations && Array.isArray(parsed.operations)) {
            // Valid intent JSON found - extract explanation
            const explanation = responseText.substring(0, jsonStartIdx).trim();

            result.explanation = explanation || 'LLM response (missing explanation)';
            result.intent = parsed;
            result.warning = 'Intent found without markers - LLM should use <<<INTENT>>> markers';

            console.warn('[parseIntentResponse] Found intent JSON without markers - LLM should use <<<INTENT>>> and <<<END_INTENT>>> markers');
            console.warn('[parseIntentResponse] Extracted explanation:', explanation);
            console.warn('[parseIntentResponse] Extracted intent:', parsed);
            return result;
          }
        } catch (err) {
          console.warn('[parseIntentResponse] Failed to parse detected JSON:', err);
          // Not valid JSON, fall through to informational response
        }
      }
    }

    // No JSON found - this is an informational response
    result.explanation = responseText.trim();
    result.warning = 'No intent markers found - this appears to be an informational response';
    return result;
  }

  // If only one marker is present, that's an error
  if (hasStartMarker !== hasEndMarker) {
    result.error = 'Malformed response: missing required markers (both <<<INTENT>>> and <<<END_INTENT>>> are required)';
    result.explanation = responseText.trim();
    return result;
  }

  // Extract sections
  const startMarkerIndex = responseText.indexOf('<<<INTENT>>>');
  const endMarkerIndex = responseText.indexOf('<<<END_INTENT>>>');

  // Validate marker order
  if (startMarkerIndex >= endMarkerIndex) {
    result.error = 'Malformed response: <<<INTENT>>> must come before <<<END_INTENT>>>';
    result.explanation = responseText.trim();
    return result;
  }

  // Check for multiple INTENT blocks (common mistake)
  const secondStartMarker = responseText.indexOf('<<<INTENT>>>', startMarkerIndex + 1);
  if (secondStartMarker !== -1) {
    result.error = 'Multiple <<<INTENT>>> blocks detected. Please combine ALL operations into a SINGLE intent block with one operations array.';
    result.explanation = responseText.trim();
    result.warning = 'The LLM created multiple INTENT blocks instead of combining operations into one block.';
    return result;
  }

  // Extract explanation (everything before <<<INTENT>>>)
  const explanation = responseText.substring(0, startMarkerIndex).trim();
  result.explanation = explanation;

  // Extract intent JSON (between markers)
  const intentStart = startMarkerIndex + '<<<INTENT>>>'.length;
  const intentEnd = endMarkerIndex;
  let intentJson = responseText.substring(intentStart, intentEnd).trim();

  // Check for common JSON mistakes (comments) and try to strip them
  const hasComments = intentJson.includes('//') || intentJson.includes('/*');
  if (hasComments) {
    console.warn('[parseIntentResponse] LLM added comments to JSON - attempting to strip them (this should NOT happen!)');

    // Try to strip comments as a fallback
    try {
      intentJson = stripJsonComments(intentJson);
      result.warning = 'LLM incorrectly added comments to JSON - they were automatically removed. Please update the prompt to prevent this.';
    } catch (stripErr) {
      result.error = 'Invalid JSON: Comments are not allowed in JSON. Remove all // and /* */ comments. Put explanations outside the <<<INTENT>>> markers using markdown instead.';
      result.explanation = explanation;
      return result;
    }
  }

  // Parse JSON
  try {
    const parsed = JSON.parse(intentJson);

    // Validate structure
    if (!parsed || typeof parsed !== 'object') {
      result.error = 'Invalid intent: not a JSON object';
      return result;
    }

    if (!Array.isArray(parsed.operations)) {
      result.error = 'Invalid intent: "operations" field must be an array';
      return result;
    }

    // Basic validation of operations
    const validationErrors = validateIntentOperations(parsed.operations);
    if (validationErrors.length > 0) {
      result.error = 'Invalid intent operations: ' + validationErrors.join(', ');
      return result;
    }

    result.intent = parsed;
  } catch (err) {
    result.error = `Failed to parse intent JSON: ${err.message}`;
    return result;
  }

  return result;
}

/**
 * Validate intent operations structure
 *
 * @param {Array} operations - Array of operations
 * @returns {Array} - Array of error messages (empty if valid)
 */
function validateIntentOperations(operations) {
  const errors = [];

  if (!Array.isArray(operations)) {
    errors.push('operations must be an array');
    return errors;
  }

  operations.forEach((op, index) => {
    if (!op || typeof op !== 'object') {
      errors.push(`Operation ${index} is not an object`);
      return;
    }

    if (!op.type || typeof op.type !== 'string') {
      errors.push(`Operation ${index} missing or invalid "type" field`);
      return;
    }

    // Validate based on operation type
    switch (op.type) {
      case 'ADD_NODE':
        if (!op.nodeType) {
          errors.push(`ADD_NODE operation ${index} missing "nodeType" field`);
        }
        if (!op.position || typeof op.position !== 'object') {
          errors.push(`ADD_NODE operation ${index} missing or invalid "position" field`);
        }
        if (!op.data || typeof op.data !== 'object') {
          errors.push(`ADD_NODE operation ${index} missing or invalid "data" field`);
        }
        break;

      case 'UPDATE_NODE':
        if (!op.nodeId) {
          errors.push(`UPDATE_NODE operation ${index} missing "nodeId" field`);
        }
        if (!op.updates || typeof op.updates !== 'object') {
          errors.push(`UPDATE_NODE operation ${index} missing or invalid "updates" field`);
        }
        break;

      case 'REMOVE_NODE':
        if (!op.nodeId) {
          errors.push(`REMOVE_NODE operation ${index} missing "nodeId" field`);
        }
        break;

      case 'ADD_BRANCH':
        if (!op.conditionSource) {
          errors.push(`ADD_BRANCH operation ${index} missing "conditionSource" field`);
        }
        if (!Array.isArray(op.branches) || op.branches.length === 0) {
          errors.push(`ADD_BRANCH operation ${index} missing or empty "branches" array`);
        }
        break;

      case 'REMOVE_BRANCH':
        if (!op.nodeId) {
          errors.push(`REMOVE_BRANCH operation ${index} missing "nodeId" field`);
        }
        break;

      default:
        errors.push(`Unknown operation type: ${op.type} at index ${index}`);
    }
  });

  return errors;
}

/**
 * Check if response has proper intent format (quick validation)
 *
 * @param {string} responseText - LLM response text
 * @returns {boolean} - True if response has intent markers
 */
export function hasIntentMarkers(responseText) {
  if (!responseText || typeof responseText !== 'string') {
    return false;
  }

  return responseText.includes('<<<INTENT>>>') &&
         responseText.includes('<<<END_INTENT>>>');
}

/**
 * Extract just the explanation text (without intent JSON)
 *
 * @param {string} responseText - LLM response text
 * @returns {string} - Explanation text
 */
export function extractExplanation(responseText) {
  if (!responseText || typeof responseText !== 'string') {
    return '';
  }

  const startMarkerIndex = responseText.indexOf('<<<INTENT>>>');

  if (startMarkerIndex === -1) {
    // No intent markers, return entire text
    return responseText.trim();
  }

  // Return everything before <<<INTENT>>>
  return responseText.substring(0, startMarkerIndex).trim();
}

/**
 * Validate intent response format (for debugging)
 *
 * @param {string} responseText - LLM response text
 * @returns {Object} - { isValid, errors, warnings }
 */
export function validateIntentResponse(responseText) {
  const errors = [];
  const warnings = [];

  if (!responseText || typeof responseText !== 'string') {
    errors.push('Response is empty or not a string');
    return { isValid: false, errors, warnings };
  }

  const hasStart = responseText.includes('<<<INTENT>>>');
  const hasEnd = responseText.includes('<<<END_INTENT>>>');

  if (!hasStart && !hasEnd) {
    warnings.push('No intent markers found - appears to be informational response');
    return { isValid: true, errors, warnings };
  }

  if (hasStart && !hasEnd) {
    errors.push('Missing <<<END_INTENT>>> marker');
  }

  if (!hasStart && hasEnd) {
    errors.push('Missing <<<INTENT>>> marker');
  }

  if (hasStart && hasEnd) {
    const startIndex = responseText.indexOf('<<<INTENT>>>');
    const endIndex = responseText.indexOf('<<<END_INTENT>>>');

    if (startIndex >= endIndex) {
      errors.push('Markers in wrong order');
    }

    // Try to parse JSON
    const intentStart = startIndex + '<<<INTENT>>>'.length;
    const intentJson = responseText.substring(intentStart, endIndex).trim();

    try {
      const parsed = JSON.parse(intentJson);

      if (!parsed.operations) {
        errors.push('Intent missing "operations" field');
      } else if (!Array.isArray(parsed.operations)) {
        errors.push('"operations" must be an array');
      } else {
        const opErrors = validateIntentOperations(parsed.operations);
        errors.push(...opErrors);
      }
    } catch (err) {
      errors.push(`Invalid JSON: ${err.message}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Strip single-line and multi-line comments from JSON string
 * This is a FALLBACK for when LLM incorrectly adds comments
 *
 * @param {string} jsonString - JSON string with comments
 * @returns {string} - JSON string without comments
 */
function stripJsonComments(jsonString) {
  let result = '';
  let inString = false;
  let inSingleLineComment = false;
  let inMultiLineComment = false;
  let stringChar = null;
  let i = 0;

  while (i < jsonString.length) {
    const char = jsonString[i];
    const nextChar = jsonString[i + 1];

    // Handle strings (don't strip comments inside strings)
    if (!inSingleLineComment && !inMultiLineComment) {
      if ((char === '"' || char === "'") && (i === 0 || jsonString[i - 1] !== '\\')) {
        if (!inString) {
          inString = true;
          stringChar = char;
          result += char;
          i++;
          continue;
        } else if (char === stringChar) {
          inString = false;
          stringChar = null;
          result += char;
          i++;
          continue;
        }
      }
    }

    // If we're in a string, just copy the character
    if (inString) {
      result += char;
      i++;
      continue;
    }

    // Handle multi-line comment start
    if (!inSingleLineComment && !inMultiLineComment && char === '/' && nextChar === '*') {
      inMultiLineComment = true;
      i += 2;
      continue;
    }

    // Handle multi-line comment end
    if (inMultiLineComment && char === '*' && nextChar === '/') {
      inMultiLineComment = false;
      i += 2;
      continue;
    }

    // Handle single-line comment start
    if (!inSingleLineComment && !inMultiLineComment && char === '/' && nextChar === '/') {
      inSingleLineComment = true;
      i += 2;
      continue;
    }

    // Handle single-line comment end (newline)
    if (inSingleLineComment && (char === '\n' || char === '\r')) {
      inSingleLineComment = false;
      result += char; // Keep the newline
      i++;
      continue;
    }

    // Skip characters that are part of comments
    if (inSingleLineComment || inMultiLineComment) {
      i++;
      continue;
    }

    // Copy normal characters
    result += char;
    i++;
  }

  return result;
}
